import type { CelestialData } from '@/services/celestial-service';
import { getCelestialData } from '@/services/celestial-service';
import type { WeatherConditionsDisplay, WeatherData } from '@/services/weather-service';
import {
  computeConditionsDisplay,
  hpaToInHg,
  isPressureInSweetSpot,
  windDegToCardinal,
} from '@/services/weather-service';
import type {
  VariableScore,
  QualityLevel,
  ConditionsQualityResult,
  FavorabilityPoint,
  PerDayTier,
} from '@/types/conditions';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BASE_WEIGHTS = {
  temperature: 0.40,
  pressure: 0.20,
  wind: 0.15,
  crepuscular: 0.25,
} as const;

const DISCLAIMER =
  'Hunt rating based on weather conditions — individual property and deer patterns vary';

const LEVEL_DESCRIPTIONS: Record<QualityLevel, string> = {
  excellent: 'Conditions are highly favorable for hunting',
  good: 'Conditions are favorable for hunting',
  fair: 'Conditions are moderate',
  poor: 'Conditions are unfavorable',
};

const LEVEL_LABELS: Record<QualityLevel, string> = {
  excellent: 'PRIME',
  good: 'GOOD',
  fair: 'FAIR',
  poor: 'POOR',
};

// ---------------------------------------------------------------------------
// Per-variable scorers (pure functions, exported for testing)
// ---------------------------------------------------------------------------

/**
 * Score temperature based on how far current temp falls below the seasonal average.
 * Returns null if seasonalAvg is not available — caller redistributes weight.
 */
export function scoreTemperature(
  currentTemp: number,
  dailyHigh: number | null,
  seasonalAvg: number | null,
): VariableScore | null {
  if (seasonalAvg === null) return null;

  const delta = seasonalAvg - currentTemp; // positive = current is below avg (favorable)
  const roundedTemp = Math.round(currentTemp);
  const absDelta = Math.round(Math.abs(delta));

  let score: number;
  let label: string;

  if (delta >= 10) {
    score = 100;
    label = `${absDelta}°F below seasonal average`;
  } else if (delta >= 5) {
    score = 70;
    label = `${absDelta}°F below seasonal average`;
  } else if (delta > -5) {
    score = 40;
    if (Math.abs(delta) < 1) {
      label = 'Near seasonal average';
    } else if (delta > 0) {
      label = `${absDelta}°F below seasonal average`;
    } else {
      label = `${absDelta}°F above seasonal average`;
    }
  } else {
    score = 10;
    label = `${absDelta}°F above seasonal average`;
  }

  const rawParts: string[] = [`${roundedTemp}°F`];
  if (dailyHigh !== null) {
    rawParts.push(`high ${Math.round(dailyHigh)}°F`);
  }

  return {
    variable: 'temperature',
    score,
    weight: BASE_WEIGHTS.temperature,
    label,
    raw: rawParts.join(' · '),
  };
}

/**
 * Score barometric pressure based on value and trend.
 * Sweet spot: 29.90–30.30 inHg (ADR-017).
 */
export function scorePressure(
  pressureInHg: number,
  trend: 'rising' | 'falling' | 'steady',
): VariableScore {
  const inSweetSpot = pressureInHg >= 29.9 && pressureInHg <= 30.3;

  let score: number;
  let label: string;

  if (inSweetSpot && trend === 'rising') {
    score = 100;
    label = `${pressureInHg.toFixed(2)} inHg, rising`;
  } else if (inSweetSpot && trend === 'steady') {
    score = 70;
    label = `${pressureInHg.toFixed(2)} inHg, steady`;
  } else if (inSweetSpot && trend === 'falling') {
    score = 50;
    label = `${pressureInHg.toFixed(2)} inHg, falling`;
  } else {
    score = 20;
    const trendLabel = trend === 'rising' ? ', rising' : trend === 'falling' ? ', falling' : '';
    label = `${pressureInHg.toFixed(2)} inHg${trendLabel} (outside sweet spot)`;
  }

  return {
    variable: 'pressure',
    score,
    weight: BASE_WEIGHTS.pressure,
    label,
    raw: `${pressureInHg.toFixed(2)} inHg`,
  };
}

/**
 * Score wind speed. Moderate wind aids scent dispersal; calm or very high wind is less ideal.
 */
export function scoreWind(speedMph: number, direction?: string): VariableScore {
  let score: number;
  let label: string;

  if (speedMph >= 5 && speedMph <= 15) {
    score = 80;
    label = 'Moderate — good scent dispersal';
  } else if (speedMph < 5) {
    score = 60;
    label = 'Calm — minimal scent dispersal';
  } else if (speedMph <= 25) {
    score = 50;
    label = 'Breezy — scent dispersal variable';
  } else {
    score = 30;
    label = 'High wind — movement likely reduced';
  }

  const rawSpeed = `${Math.round(speedMph)} mph`;
  const raw = direction ? `${direction} ${rawSpeed}` : rawSpeed;

  return {
    variable: 'wind',
    score,
    weight: BASE_WEIGHTS.wind,
    label,
    raw,
  };
}

/**
 * Score proximity to crepuscular windows (civil dawn / civil dusk).
 * The crepuscular window is the most reliable activity signal (ADR-017).
 */
export function scoreCrepuscular(now: Date, civilDawn: Date, civilDusk: Date): VariableScore {
  const nowMs = now.getTime();
  const dawnMs = civilDawn.getTime();
  const duskMs = civilDusk.getTime();

  const minsFromDawn = Math.abs(nowMs - dawnMs) / (60 * 1000);
  const minsFromDusk = Math.abs(nowMs - duskMs) / (60 * 1000);
  const closestMins = Math.min(minsFromDawn, minsFromDusk);

  const isNearDawn = minsFromDawn <= minsFromDusk;
  const windowName = isNearDawn ? 'dawn' : 'dusk';

  let score: number;
  let label: string;

  if (closestMins <= 30) {
    score = 100;
    label = `Within 30 min of ${windowName}`;
  } else if (closestMins <= 60) {
    score = 85;
    const mins = Math.round(closestMins);
    label = `${mins} min from ${windowName}`;
  } else if (closestMins <= 120) {
    score = 60;
    const hrs = (closestMins / 60).toFixed(1);
    label = `${hrs} hr from ${windowName}`;
  } else {
    score = 20;
    label = 'Midday — low crepuscular activity expected';
  }

  const roundedMins = Math.round(closestMins);
  const raw = closestMins <= 90
    ? `${roundedMins} min to ${windowName}`
    : `${(closestMins / 60).toFixed(1)} hr to ${windowName}`;

  return {
    variable: 'crepuscular',
    score,
    weight: BASE_WEIGHTS.crepuscular,
    label,
    raw,
  };
}

// ---------------------------------------------------------------------------
// Composite computation
// ---------------------------------------------------------------------------

/**
 * Map a 0–100 composite score to its QualityLevel tier. Single source of truth
 * for the score→level ladder — used by both computeWeightedScore and
 * computePerDayTiers so the thresholds never diverge.
 */
export function scoreToLevel(total: number): QualityLevel {
  if (total >= 80) return 'excellent';
  if (total >= 60) return 'good';
  if (total >= 40) return 'fair';
  return 'poor';
}

/**
 * Compute the weighted composite score from an array of variable scores.
 * The weights in the VariableScore objects must already sum to 1.0.
 */
export function computeWeightedScore(scores: VariableScore[]): { total: number; level: QualityLevel } {
  const total = Math.round(
    scores.reduce((sum, v) => sum + v.score * v.weight, 0),
  );

  return { total, level: scoreToLevel(total) };
}

/**
 * Main entry point. Synthesizes weather and celestial data into a quality score.
 * Returns null when weather data is unavailable — celestial-only data is not sufficient.
 *
 * The `now` parameter defaults to the current time but is injectable for deterministic testing.
 * The `seasonalAvgTemp` parameter is nullable: when absent, the temperature weight is
 * redistributed proportionally to the remaining three variables.
 */
export function computeConditionsQuality(
  weather: WeatherConditionsDisplay | null,
  celestial: CelestialData,
  now: Date = new Date(),
  seasonalAvgTemp: number | null = null,
): ConditionsQualityResult | null {
  if (weather === null) return null;

  const tempScore = scoreTemperature(weather.temp, weather.tempToday, seasonalAvgTemp);
  const pressureScore = scorePressure(weather.pressure, weather.pressureTrend ?? 'steady');
  const windScore = scoreWind(weather.windSpeed, weather.windDirection);
  const crepScore = scoreCrepuscular(now, celestial.sun.civilDawn, celestial.sun.civilDusk);

  let variables: VariableScore[];

  if (tempScore !== null) {
    variables = [tempScore, pressureScore, windScore, crepScore];
  } else {
    // Redistribute temperature weight (0.40) proportionally across remaining variables.
    // Remaining base weights: pressure 0.20, wind 0.15, crepuscular 0.25 → sum 0.60
    const remainingSum = BASE_WEIGHTS.pressure + BASE_WEIGHTS.wind + BASE_WEIGHTS.crepuscular;
    variables = [
      { ...pressureScore, weight: BASE_WEIGHTS.pressure / remainingSum },
      { ...windScore, weight: BASE_WEIGHTS.wind / remainingSum },
      { ...crepScore, weight: BASE_WEIGHTS.crepuscular / remainingSum },
    ];
  }

  const { total, level } = computeWeightedScore(variables);

  return {
    score: total,
    level,
    label: LEVEL_LABELS[level],
    description: LEVEL_DESCRIPTIONS[level],
    variables,
    computedAt: now,
    disclaimer: DISCLAIMER,
  };
}

// ---------------------------------------------------------------------------
// Per-step favorability across the legal-light window (DASH-T1)
// ---------------------------------------------------------------------------

const FAVORABILITY_MIN_STEPS = 10;
const FAVORABILITY_MAX_STEPS = 14;
const MS_PER_HOUR = 3_600_000;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Compute a series of per-step conditions-quality scores spanning the legal-light
 * window (civil dawn → civil dusk) for the given day at the given coordinates.
 *
 * Pure and synchronous. Reuses the SHIPPED scoring pipeline unchanged:
 * `computeConditionsDisplay` (weather projection) → `computeConditionsQuality`
 * (composite score). The number of steps N is the rounded span in hours + 1,
 * clamped to [10, 14]. Step 0 and step N-1 land exactly on civilDawn / civilDusk;
 * interior steps are evenly interpolated. seasonalAvgTemp is always null (the
 * temperature weight is redistributed by the shipped engine).
 *
 * Returns [] when no weather data is available — the empty check lives here
 * because `computeConditionsDisplay` falls back to `current` on empty hourly and
 * does not signal emptiness.
 */
export function computeFavorabilityByPeriod(
  weather: WeatherData,
  day: Date,
  lat: number,
  lng: number,
): FavorabilityPoint[] {
  if (weather == null || weather.hourly.length === 0) return [];

  const celestial = getCelestialData(day, lat, lng);
  const dawn = celestial.sun.civilDawn;
  const dusk = celestial.sun.civilDusk;

  const spanHours = (dusk.getTime() - dawn.getTime()) / MS_PER_HOUR;
  const N = clamp(Math.round(spanHours) + 1, FAVORABILITY_MIN_STEPS, FAVORABILITY_MAX_STEPS);

  const series: FavorabilityPoint[] = [];

  for (let i = 0; i < N; i++) {
    let stepDate: Date;
    if (i === 0) {
      stepDate = new Date(dawn.getTime());
    } else if (i === N - 1) {
      stepDate = new Date(dusk.getTime());
    } else {
      const stepMs = dawn.getTime() + (i / (N - 1)) * (dusk.getTime() - dawn.getTime());
      stepDate = new Date(stepMs);
    }

    const display = computeConditionsDisplay(weather, stepDate.getTime());
    const result = computeConditionsQuality(display, celestial, stepDate, null);

    if (result !== null) {
      series.push({ t: stepDate.toISOString(), score: result.score });
    }
  }

  return series;
}

// ---------------------------------------------------------------------------
// Per-day tier scoring across the forecast horizon (DASH-T4)
// ---------------------------------------------------------------------------

const PER_DAY_MAX_DAYS = 8;
const MS_PER_SECOND = 1000;

/**
 * Title-case a raw OWM condition detail (e.g. "overcast clouds" → "Overcast
 * Clouds"). Mirrors the exact inline expression in weather-service's
 * computeConditionsDisplay so FAR-day displays read identically to the near
 * path — do NOT introduce a separate util or diverge from this expression.
 */
function titleCaseCondition(detail: string): string {
  return detail
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Bucket each forecast day into a quality tier (DASH-T4).
 *
 * Pure and synchronous. Reuses the SHIPPED scoring pipeline unchanged. Per day,
 * a coverage-derived predicate decides NEAR vs FAR — it is NEVER a hardcoded day
 * index:
 *
 *   NEAR  iff the per-step series is non-empty AND the last hourly entry's
 *         timestamp reaches at least that day's civil dusk (the hourly horizon
 *         covers the legal-light window). Score = peak of the per-step series.
 *   FAR   otherwise. Score = composite of the daily aggregate run through the
 *         shipped computeConditionsQuality pipeline (Option-A), with `now` pinned
 *         to civil dusk (dusk IS a crepuscular window, holding it at peak) and
 *         seasonalAvgTemp = null (temperature weight redistributed).
 *
 * Returns [] when no weather data is available (null/undefined or empty daily) —
 * never fabricates days. Capped at 8 days. Live-only: no persistence, no Dexie,
 * no LogEntryConditionsQuality write, no sync. Moon is never referenced.
 */
export function computePerDayTiers(weather: WeatherData, lat: number, lng: number): PerDayTier[] {
  // Use == to catch both null and undefined, matching computeFavorabilityByPeriod.
  if (weather == null || weather.daily.length === 0) return [];

  const tiers: PerDayTier[] = [];
  const dayCount = Math.min(weather.daily.length, PER_DAY_MAX_DAYS);
  const lastHourlyMs = weather.hourly.length > 0
    ? weather.hourly[weather.hourly.length - 1].dt * MS_PER_SECOND
    : -Infinity;

  for (let i = 0; i < dayCount; i++) {
    const daily = weather.daily[i];
    const dayDate = new Date(daily.dt * MS_PER_SECOND); // dt is Unix SECONDS
    const civilDusk = getCelestialData(dayDate, lat, lng).sun.civilDusk;
    const series = computeFavorabilityByPeriod(weather, dayDate, lat, lng);

    // NEAR/FAR is coverage-derived: a day is NEAR when the hourly horizon reaches
    // its civil dusk (and the per-step series exists). This is the hourly-horizon-
    // vs-civil-dusk coverage rule — never an index cutoff.
    const isNear = series.length > 0 && lastHourlyMs >= civilDusk.getTime();

    let score: number;
    let tier: QualityLevel;

    if (isNear) {
      // NEAR: peak of the T1 per-step series.
      score = Math.max(...series.map((p) => p.score));
      tier = scoreToLevel(score);
    } else {
      // FAR (Option-A): project the daily aggregate into a display and run it
      // through the shipped pipeline. pressureTrend 'steady' is load-bearing —
      // the pressure scorer reads it.
      const pressureInHg = hpaToInHg(daily.pressure);
      const display: WeatherConditionsDisplay = {
        temp: daily.tempDay,
        feelsLike: daily.tempDay,
        tempToday: daily.tempMax,
        tempDelta: null,
        tempDeltaFavorable: false,
        pressure: pressureInHg,
        pressureTrend: 'steady',
        pressureInSweetSpot: isPressureInSweetSpot(pressureInHg),
        windSpeed: daily.windSpeed,
        windDeg: daily.windDeg,
        windDirection: windDegToCardinal(daily.windDeg),
        humidity: daily.humidity,
        condition: titleCaseCondition(daily.conditionDetail),
        icon: daily.icon,
        lastUpdated: new Date(weather.fetchedAt), // fetchedAt is a ms number
      };

      const celestial = getCelestialData(dayDate, lat, lng);
      // Passing civilDusk as `now` holds crepuscular at peak (dusk is a window);
      // seasonalAvgTemp = null keeps temperature redistributed.
      const result = computeConditionsQuality(display, celestial, civilDusk, null);

      // Defensive: cannot be null for a non-null display — skip the day if so.
      if (result === null) continue;

      score = result.score;
      tier = result.level;
    }

    tiers.push({ date: dayDate.toISOString(), tier, score });
  }

  return tiers;
}
